import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"
import { Address } from "viem"

import { frontendUserPairInfoAbi } from "@/domains/abis"
import { LcAddress } from "@/domains/core"
import { MinimalPoolStore } from "@/domains/minimal-pool"

export type ScalensUserInfoPairs = {
  pairAddress: LcAddress
  userPairBalance: bigint
  userPairReserve0: bigint
  userPairReserve1: bigint
  userPairClaimableFees0: bigint
  userPairClaimableFees1: bigint
  userGaugeBalance: bigint
  userGaugeReserve0: bigint
  userGaugeReserve1: bigint
  userGaugeGovernanceEarned: bigint
}

/**
 * Load user info pairs for a single chunk of pools.
 * Chunking is handled by the caller (typically via RPC strategy).
 */
export const scalensUserInfoPairs = async ({
  who,
  scalensUserInfoPairs,
  pools, // Single chunk of pools
  config,
}: {
  who: LcAddress
  scalensUserInfoPairs: LcAddress
  pools: Array<MinimalPoolStore> // Single chunk
  config: Config
}): Promise<Array<ScalensUserInfoPairs>> => {
  if (pools.length === 0) {
    return []
  }

  // Make the RPC call for this chunk
  const addresses = pools.map((pool) => pool.address.toViemAddress())
  const response = (await readContract(config, {
    abi: frontendUserPairInfoAbi,
    address: scalensUserInfoPairs.toViemAddress(),
    functionName: "userInfoPairsStruct",
    args: [addresses, who.toViemAddress()],
  })) as [
    bigint,
    Array<{
      A: Address
      N: [bigint, bigint, bigint, bigint, bigint, bigint, bigint, bigint, bigint]
    }>,
  ]

  // Transform response to ScalensUserInfoPairs format
  const responseItems = response[1]
  const results: ScalensUserInfoPairs[] = []

  responseItems.forEach((result, index) => {
    const pool = pools[index]
    if (pool) {
      results.push({
        pairAddress: new LcAddress(result.A),
        userPairBalance: result.N[0],
        userPairReserve0: result.N[1],
        userPairReserve1: result.N[2],
        userPairClaimableFees0: result.N[3],
        userPairClaimableFees1: result.N[4],
        userGaugeBalance: result.N[5],
        userGaugeReserve0: result.N[6],
        userGaugeReserve1: result.N[7],
        userGaugeGovernanceEarned: result.N[8],
      })
    }
  })

  return results
}
